import { Schema } from "effect";
import { ProjectIdSchema, UserIdSchema, WorkspaceIdSchema } from "./ids";
import {
  CanonicalIdentifierSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowValidationSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";

export const VOICE_SESSION_TTL_MS = 15 * 60 * 1_000;
export const VOICE_TICKET_TTL_MS = VOICE_SESSION_TTL_MS;
export const VOICE_NOTE_TITLE_MAX_BYTES = 160;
export const VOICE_TURN_TEXT_MAX_BYTES = 8 * 1_024;
export const VOICE_TRANSCRIPT_MAX_BYTES = 256 * 1_024;
export const VOICE_MAX_TURNS = 500;
export const VOICE_MAX_KEYWORDS = 16;
export const VOICE_KEYWORD_MAX_BYTES = 40;
export const VOICE_AUDIO_MAX_BYTES = 25 * 1_024 * 1_024;
export const VOICE_TICKET_ISSUER = "papersflow-voice";
export const VOICE_MUSE_MODEL = "muse-voice-transcribe-1.0";
export const VOICE_MUSE_ASR_HTTPS_ORIGIN = "https://api.meta.ai";
export const VOICE_MUSE_ASR_PATH = "/v1/asr/realtime";
export const VOICE_PCM_RATE_HZ = 24_000;
export const VOICE_PCM_FRAME_MS = 80;
export const VOICE_PCM_FRAME_BYTES = (VOICE_PCM_RATE_HZ * 2 * VOICE_PCM_FRAME_MS) / 1_000;
export const VOICE_REALTIME_PATH_PREFIX = "/v1/notes/";
export const VOICE_REALTIME_PATH_SUFFIX = "/realtime";
export const VOICE_AUDIO_PATH_SUFFIX = "/audio";

const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

function boundedText(maximumBytes: number, options?: { readonly allowEmpty?: boolean }) {
  const allowEmpty = options?.allowEmpty === true;
  return Schema.String.check(
    Schema.makeFilter((value) => {
      if (value !== value.trim() || utf8ByteLength(value) > maximumBytes) {
        return `must be trimmed and at most ${maximumBytes} UTF-8 bytes`;
      }
      if (!allowEmpty && value.length === 0) return "must be non-empty";
      return true;
    }),
  );
}

export const VoiceNoteIdSchema = CanonicalIdentifierSchema.pipe(Schema.brand("VoiceNoteId"));
export const VoiceNoteTurnIdSchema = CanonicalIdentifierSchema.pipe(Schema.brand("VoiceNoteTurnId"));
export const VoiceNoteStatusSchema = Schema.Literals([
  "recording",
  "processing",
  "ready",
  "failed",
]);
export const VoiceNoteSurfaceSchema = Schema.Literals(["note", "composer"]);
export const VoiceTranscriptionModeSchema = Schema.Literals(["ENDPOINTING", "DIARIZATION"]);
export const VoiceNoteTitleSchema = boundedText(VOICE_NOTE_TITLE_MAX_BYTES, { allowEmpty: true });
export const VoiceTurnTextSchema = boundedText(VOICE_TURN_TEXT_MAX_BYTES);
export const VoicePartialTextSchema = boundedText(VOICE_TURN_TEXT_MAX_BYTES, { allowEmpty: true });
export const VoiceTranscriptSchema = boundedText(VOICE_TRANSCRIPT_MAX_BYTES, { allowEmpty: true });
export const VoiceKeywordSchema = boundedText(VOICE_KEYWORD_MAX_BYTES);
export const VoiceKeywordsSchema = Schema.Array(VoiceKeywordSchema).check(
  Schema.isMaxLength(VOICE_MAX_KEYWORDS),
);
export const VoiceTimestampSchema = NonNegativeSafeIntegerSchema;
export const VoiceDurationMsSchema = NonNegativeSafeIntegerSchema;
export const VoiceTurnIndexSchema = NonNegativeSafeIntegerSchema.check(
  Schema.isLessThan(VOICE_MAX_TURNS),
);
export const VoiceAudioKeySchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(512),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() &&
        !value.includes("..") &&
        !value.startsWith("/") &&
        /^voice-notes\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\.(?:webm|ogg|opus)$/u.test(
          value,
        )) ||
      "must be a canonical voice-note object key",
  ),
);
export const VoiceLanguageSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(64),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() && /^[A-Za-z][A-Za-z -]{0,63}$/u.test(value)) ||
      "must be a language name",
  ),
);
export const VoicePublicErrorCodeSchema = Schema.Literals([
  "VOICE_INVALID_INPUT",
  "VOICE_UNAUTHENTICATED",
  "VOICE_FORBIDDEN",
  "VOICE_NOT_FOUND",
  "VOICE_CONFLICT",
  "VOICE_UNAVAILABLE",
  "VOICE_RATE_LIMITED",
  "VOICE_FAILED",
]);
export const VoicePublicErrorSchema = Schema.Struct({
  code: VoicePublicErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export const VoiceNoteSchema = Schema.Struct({
  noteId: VoiceNoteIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  createdBy: UserIdSchema,
  surface: VoiceNoteSurfaceSchema,
  mode: VoiceTranscriptionModeSchema,
  status: VoiceNoteStatusSchema,
  title: Schema.optionalKey(VoiceNoteTitleSchema),
  transcript: Schema.optionalKey(VoiceTranscriptSchema),
  durationMs: Schema.optionalKey(VoiceDurationMsSchema),
  audioKey: Schema.optionalKey(VoiceAudioKeySchema),
  language: Schema.optionalKey(VoiceLanguageSchema),
  errorMessage: Schema.optionalKey(
    Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  ),
  createdAt: VoiceTimestampSchema,
  updatedAt: VoiceTimestampSchema,
  archivedAt: Schema.optionalKey(VoiceTimestampSchema),
});

export const VoiceNoteTurnSchema = Schema.Struct({
  turnId: VoiceNoteTurnIdSchema,
  noteId: VoiceNoteIdSchema,
  index: VoiceTurnIndexSchema,
  text: VoiceTurnTextSchema,
  startMs: Schema.optionalKey(VoiceDurationMsSchema),
  endMs: Schema.optionalKey(VoiceDurationMsSchema),
  speaker: Schema.optionalKey(boundedText(40)),
  createdAt: VoiceTimestampSchema,
});

export const VoiceSessionTicketPayloadSchema = Schema.Struct({
  issuer: Schema.Literal(VOICE_TICKET_ISSUER),
  noteId: VoiceNoteIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  actorUserId: UserIdSchema,
  surface: VoiceNoteSurfaceSchema,
  mode: VoiceTranscriptionModeSchema,
  keywords: VoiceKeywordsSchema,
  issuedAt: PositiveSafeIntegerSchema,
  expiresAt: PositiveSafeIntegerSchema,
}).check(
  Schema.makeFilter(
    (payload) =>
      payload.expiresAt === payload.issuedAt + VOICE_TICKET_TTL_MS ||
      "voice ticket must use the fixed session lifetime",
  ),
);

export const VoiceSessionTicketSchema = Schema.String.check(
  Schema.isMinLength(80),
  Schema.isMaxLength(8_192),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9_-]+\.[a-f0-9]{64}$/u.test(value) || "must be a signed voice session ticket",
  ),
);

export const VoiceStartSessionRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  surface: VoiceNoteSurfaceSchema,
  mode: VoiceTranscriptionModeSchema,
});

export const VoiceStartSessionResultSchema = Schema.Struct({
  noteId: VoiceNoteIdSchema,
  websocketUrl: Schema.String.check(Schema.isMinLength(8), Schema.isMaxLength(2_048)),
  audioUploadUrl: Schema.String.check(Schema.isMinLength(8), Schema.isMaxLength(2_048)),
  ticket: VoiceSessionTicketSchema,
  expiresAt: PositiveSafeIntegerSchema,
  surface: VoiceNoteSurfaceSchema,
  mode: VoiceTranscriptionModeSchema,
});

export const VoiceClientAuthFrameSchema = Schema.Struct({
  type: Schema.Literal("sessionAuth"),
  ticket: VoiceSessionTicketSchema,
});

export const VoiceClientEndStreamFrameSchema = Schema.Struct({
  type: Schema.Literal("endStream"),
});

export const VoiceClientTextFrameSchema = Schema.Union([
  VoiceClientAuthFrameSchema,
  VoiceClientEndStreamFrameSchema,
]);

export const VoicePartialEventSchema = Schema.Struct({
  type: Schema.Literal("partial"),
  text: VoicePartialTextSchema,
});

export const VoiceSpeechStartEventSchema = Schema.Struct({
  type: Schema.Literal("speechStart"),
  turnId: NonNegativeSafeIntegerSchema,
});

export const VoiceSpeechEndEventSchema = Schema.Struct({
  type: Schema.Literal("speechEnd"),
  turnId: NonNegativeSafeIntegerSchema,
});

export const VoiceSpeechCompleteEventSchema = Schema.Struct({
  type: Schema.Literal("speechComplete"),
  turnId: NonNegativeSafeIntegerSchema,
  text: VoiceTurnTextSchema,
  startMs: Schema.optionalKey(VoiceDurationMsSchema),
  endMs: Schema.optionalKey(VoiceDurationMsSchema),
});

export const VoiceSpeakerEventSchema = Schema.Struct({
  type: Schema.Literal("speaker"),
  turnId: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  speaker: boundedText(40),
});

export const VoiceErrorEventSchema = Schema.Struct({
  type: Schema.Literal("error"),
  reason: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
});

export const VoiceSessionReadyEventSchema = Schema.Struct({
  type: Schema.Literal("sessionReady"),
});

export const VoiceSessionEndedEventSchema = Schema.Struct({
  type: Schema.Literal("sessionEnded"),
});

export const VoiceEventSchema = Schema.Union([
  VoiceSessionReadyEventSchema,
  VoicePartialEventSchema,
  VoiceSpeechStartEventSchema,
  VoiceSpeechEndEventSchema,
  VoiceSpeechCompleteEventSchema,
  VoiceSpeakerEventSchema,
  VoiceErrorEventSchema,
  VoiceSessionEndedEventSchema,
]);

export const VoiceAppendTurnRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  noteId: VoiceNoteIdSchema,
  text: VoiceTurnTextSchema,
  startMs: Schema.optionalKey(VoiceDurationMsSchema),
  endMs: Schema.optionalKey(VoiceDurationMsSchema),
  speaker: Schema.optionalKey(boundedText(40)),
});

export const VoiceCompleteSessionRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  noteId: VoiceNoteIdSchema,
  durationMs: Schema.optionalKey(VoiceDurationMsSchema),
  language: Schema.optionalKey(VoiceLanguageSchema),
});

export const VoiceFailSessionRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  noteId: VoiceNoteIdSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
});

export const VoiceAttachAudioRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  noteId: VoiceNoteIdSchema,
  audioKey: VoiceAudioKeySchema,
});

export const VoiceRenameNoteRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  noteId: VoiceNoteIdSchema,
  title: VoiceNoteTitleSchema,
});

export function voiceRealtimePath(noteId: string): string {
  return `${VOICE_REALTIME_PATH_PREFIX}${noteId}${VOICE_REALTIME_PATH_SUFFIX}`;
}

export function voiceAudioPath(noteId: string): string {
  return `${VOICE_REALTIME_PATH_PREFIX}${noteId}${VOICE_AUDIO_PATH_SUFFIX}`;
}

export function voiceAudioObjectKey(input: {
  readonly workspaceId: string;
  readonly projectId: string;
  readonly noteId: string;
  readonly extension: "webm" | "ogg" | "opus";
}): string {
  return `voice-notes/${input.workspaceId}/${input.projectId}/${input.noteId}.${input.extension}`;
}

export function voiceWorkerHttpOriginIssue(value: string): true | string {
  if (value !== value.trim() || utf8ByteLength(value) > 2_048) {
    return "must be a bounded HTTP origin";
  }
  try {
    const url = new URL(value);
    if (
      (url.protocol !== "http:" && url.protocol !== "https:") ||
      url.username !== "" ||
      url.password !== "" ||
      url.href !== `${url.origin}/`
    ) {
      return "must be a credential-free HTTP origin";
    }
    return true;
  } catch {
    return "must be a credential-free HTTP origin";
  }
}

function switchUrlProtocol(origin: string, protocol: "http:" | "https:" | "ws:" | "wss:"): string {
  const url = new URL(origin);
  url.protocol = protocol;
  return url.origin;
}

export function voiceRealtimeUrl(origin: string, noteId: string): string {
  if (voiceWorkerHttpOriginIssue(origin) !== true) {
    throw new Error("Voice worker origin is invalid");
  }
  const socketOrigin =
    new URL(origin).protocol === "https:"
      ? switchUrlProtocol(origin, "wss:")
      : switchUrlProtocol(origin, "ws:");
  return `${socketOrigin}${voiceRealtimePath(noteId)}`;
}

export function voiceAudioUploadUrl(origin: string, noteId: string): string {
  if (voiceWorkerHttpOriginIssue(origin) !== true) {
    throw new Error("Voice worker origin is invalid");
  }
  return `${new URL(origin).origin}${voiceAudioPath(noteId)}`;
}

export const decodeVoiceNote = Schema.decodeUnknownSync(
  VoiceNoteSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeVoiceNote = Schema.encodeSync(VoiceNoteSchema, PapersFlowBoundaryParseOptions);
export const decodeVoiceNoteTurn = Schema.decodeUnknownSync(
  VoiceNoteTurnSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeVoiceNoteTurn = Schema.encodeSync(
  VoiceNoteTurnSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceSessionTicketPayload = Schema.decodeUnknownSync(
  VoiceSessionTicketPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeVoiceSessionTicketPayload = Schema.encodeSync(
  VoiceSessionTicketPayloadSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceStartSessionRequest = Schema.decodeUnknownSync(
  VoiceStartSessionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceStartSessionResult = Schema.decodeUnknownSync(
  VoiceStartSessionResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeVoiceStartSessionResult = Schema.encodeSync(
  VoiceStartSessionResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceClientTextFrame = Schema.decodeUnknownSync(
  VoiceClientTextFrameSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceEvent = Schema.decodeUnknownSync(
  VoiceEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeVoiceEvent = Schema.encodeSync(VoiceEventSchema, PapersFlowBoundaryParseOptions);
export const decodeVoiceAppendTurnRequest = Schema.decodeUnknownSync(
  VoiceAppendTurnRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceCompleteSessionRequest = Schema.decodeUnknownSync(
  VoiceCompleteSessionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceFailSessionRequest = Schema.decodeUnknownSync(
  VoiceFailSessionRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeVoiceAttachAudioRequest = Schema.decodeUnknownSync(
  VoiceAttachAudioRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const VoiceEventValidationSchema = toPapersFlowValidationSchema(VoiceEventSchema);
export const VoiceSessionTicketPayloadValidationSchema = toPapersFlowValidationSchema(
  VoiceSessionTicketPayloadSchema,
);

export type VoiceNoteId = typeof VoiceNoteIdSchema.Type;
export type VoiceNoteIdEncoded = typeof VoiceNoteIdSchema.Encoded;
export type VoiceNoteTurnId = typeof VoiceNoteTurnIdSchema.Type;
export type VoiceNoteStatus = typeof VoiceNoteStatusSchema.Type;
export type VoiceNoteSurface = typeof VoiceNoteSurfaceSchema.Type;
export type VoiceTranscriptionMode = typeof VoiceTranscriptionModeSchema.Type;
export type VoiceNote = typeof VoiceNoteSchema.Type;
export type VoiceNoteEncoded = typeof VoiceNoteSchema.Encoded;
export type VoiceNoteTurn = typeof VoiceNoteTurnSchema.Type;
export type VoiceNoteTurnEncoded = typeof VoiceNoteTurnSchema.Encoded;
export type VoiceSessionTicketPayload = typeof VoiceSessionTicketPayloadSchema.Type;
export type VoiceSessionTicketPayloadEncoded = typeof VoiceSessionTicketPayloadSchema.Encoded;
export type VoiceStartSessionRequest = typeof VoiceStartSessionRequestSchema.Type;
export type VoiceStartSessionResult = typeof VoiceStartSessionResultSchema.Type;
export type VoiceStartSessionResultEncoded = typeof VoiceStartSessionResultSchema.Encoded;
export type VoiceClientTextFrame = typeof VoiceClientTextFrameSchema.Type;
export type VoiceEvent = typeof VoiceEventSchema.Type;
export type VoiceEventEncoded = typeof VoiceEventSchema.Encoded;
export type VoicePublicErrorCode = typeof VoicePublicErrorCodeSchema.Type;
export type VoicePublicError = typeof VoicePublicErrorSchema.Type;
